<template>
  <section
    class="RunCard"
    :class="{
      'RunCard--isPast': isPast,
      'RunCard--isCanceled': !!run.canceled_at,
      'RunCard--isGameOn': isGameOn,
    }"
    :data-run-id="run.id"
    :data-testid="`runCard-${run.id}`"
  >
    <div
      class="RunCard__courtContainer"
      @click="emit('runClick')"
    >
      <CourtImage
        v-if="run.court"
        class="RunCard__courtImage"
        :modelValue="run.court"
      />

      <RunLocation
        v-if="run.court"
        class="RunCard__courtLocation"
        :court="run.court"
      />
    </div>
    <RunHeroView
      class="RunCard__hero"
      theme="light"
      :modelValue="run"
      :includeLocation="false"
      :canNavigate="canNavigate"
    >
      <template #corner>
        <button
          v-if="canNavigate"
          class="RunCard__heroArrow"
          data-testid="runCarouselItemArrow"
          type="button"
          @click="emit('runClick')"
        >
          <Icon
            class="RunCard__heroArrowIcon"
            name="chevronRight"
          />
        </button>
      </template>
      <template #footer>
        <RunPlayerAvatarListView
          v-if="!excludeAvatarList"
          class="RunCard__playerAvatarList"
          :modelValue="run"
          @click="emit('runClick')"
        />
      </template>
    </RunHeroView>

    <div class="RunCard__bannerContainer">
      <Transition>
        <div
          v-if="isBannerVisible"
          class="RunCard__banner"
          data-testid="runBanner"
        >
          <template v-if="run.canceled_at">
            Canceled
          </template>
          <template v-else-if="neededPlayerCount > 0">
            need {{ neededPlayerCount }} more
          </template>
          <template v-else-if="openSpotCount > 0">
            {{ openSpotCount }} {{ openSpotCount > 1 ? "spots" : "spot" }} left
          </template>
          <template v-else-if="isGameOn">
            Game on!
          </template>
        </div>
      </Transition>
    </div>

    <div
      v-if="$slots.actions"
      class="RunCard__actionList"
    >
      <slot name="actions" />
    </div>

    <ul
      v-if="awardWinners.length > 0"
      class="RunCard__awardWinnerList"
    >
      <li
        v-for="awardWinner in awardWinners"
        :key="awardWinner.award.id"
        class="RunCard__awardWinner"
      >
        <PlayerAvatar
          class="RunCard__awardWinnerAvatar"
          :modelValue="awardWinner.player"
          hasAward
        />
        <div class="RunCard__awardWinnerContent">
          <div class="RunCard__awardWinnerName">
            {{ awardWinner.player.name }}
          </div>
          <AwardBadge :modelValue="awardWinner.award" />
        </div>
      </li>
    </ul>
  </section>
</template>

<script lang="ts" setup>
import AwardBadge from "@/components/AwardBadge.vue";
import CourtImage from "@/components/CourtImage.vue";
import Icon from "@/components/Icon.vue";
import PlayerAvatar from "@/components/PlayerAvatar.vue";
import RunLocation from "@/components/RunLocation.vue";
import useNow from "@/composables/useNow.ts";
import useRun from "@/composables/useRun";
import isTruthy from "@/lib/utils/boolean/isTruthy.ts";
import { RunForCarousel } from "@/stores/useRunCarouselStore";
import RunHeroView from "@/views/RunHeroView.vue";
import RunPlayerAvatarListView from "@/views/RunPlayerAvatarListView.vue";
import { isBefore, startOfDay } from "date-fns";
import { computed, toRefs } from "vue";

const emit = defineEmits<{
  runClick: [],
}>();

const props = withDefaults(defineProps<{
  run: RunForCarousel,
  canNavigate?: boolean,
  excludeAvatarList?: boolean,
}>(), {
  canNavigate: true,
  excludeAvatarList: false,
});

const {
  now,
} = useNow();

const {
  run,
} = toRefs(props);

const {
  hasRunStarted,
  isGameOn,
  startDateInTimezone,
  openSpotCount,
  neededPlayerCount,
} = useRun(run);

const isBannerVisible = computed(() => !!run.value.canceled_at || !hasRunStarted.value && (isGameOn.value || openSpotCount.value > 0 || neededPlayerCount.value > 0));

// TODO: refactor with useRunSchedule
const isPast = computed(() => {
  return isBefore(startOfDay(startDateInTimezone.value), startOfDay(now.value));
});

const awardWinners = computed(() => {
  return run.value.award_winners.map((awardWinner) => {
    const player = run.value.statuses.find((status) => status.player_id === awardWinner.recipient_id)?.player;
    if (!player) return null;

    return {
      player,
      award: awardWinner.award,
    };
  }).filter(isTruthy);
});
</script>

<style lang="scss" scoped>
.RunCard {
  position: relative;
  isolation: isolate;

  display: flex;
  flex-direction: column;
  height: 100%;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);
  color: var(--themeColorWhite);

  scroll-snap-align: center;
  scroll-snap-stop: always;

  [data-theme="dark"] & {
    outline: solid 3px var(--themeColorWhite);
  }
}

.RunCard__courtContainer {
  position: relative;
  display: grid;
  transition: 350ms opacity;

  .RunCard--isCanceled & {
    opacity: 0.4;
  }
}

.RunCard__courtImage {
  height: 200px;
  overflow: hidden;

  border-radius: 6px 6px 0 0;

  transition: 350ms filter;

  .RunCard--isPast &,
  .RunCard--isCanceled & {
    filter: grayscale(100%);
  }
}

.RunCard__courtLocation {
  --iconSize: 14px;
  position: absolute;
  left: 0;
  bottom: 0;

  width: 100%;
  padding: 16px 14px;

  background: linear-gradient(to bottom, transparent, var(--themeDarkTransparent600));
  color: var(--themeColorWhite);

  font-size: 14px;
  text-shadow: 1px 1px 4px var(--themeDarkTransparent600);

  :deep(svg) {
    filter: drop-shadow(1px 1px 4px var(--themeDarkTransparent600));
  }
}

.RunCard__hero {
  flex: 1 1 auto;

  .RunCard--isCanceled & {
    opacity: 0.4;
  }
}

.RunCard__heroArrow {
  padding: 12px 6px;
}

.RunCard__heroArrowIcon {
  --iconSize: 20px;
}

.RunCard__playerAvatarList {
  transition: 350ms filter;

  .RunCard--isPast & {
    filter: grayscale(100%);
  }
}

.RunCard__bannerContainer {
  position: absolute;
  top: 0;
  right: 0;

  width: 140px;
  aspect-ratio: 1;
  overflow: hidden;

  pointer-events: none;
}

.RunCard__banner {
  position: absolute;
  top: 40px;
  right: 40px;
  transform: translate(50%, -50%) rotate(45deg);
  transform-origin: center;

  width: 100%;
  padding: 6px 0 5px;

  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-align: center;
  text-transform: uppercase;

  &.v-enter-active,
  &.v-leave-active {
    transition: 350ms cubic-bezier(0.65, 0, 0.35, 1);
    transition-property: transform, opacity;
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: translate(calc(50% + 36px), calc(-50% - 36px)) rotate(45deg);
    opacity: 0;
  }

  .RunCard--isCanceled & {
    background-color: var(--themeColorNeutral200);
    color: var(--themeColorNeutral900);
  }
}

.RunCard__actionList {
  --buttonGap: 10px;
  --buttonPaddingBlock: 14px;
  --buttonPaddingInline: 12px;
  border-top: solid 1px var(--themeColorNeutral100);

  &:empty {
    display: none;
  }
}

.RunCard__awardWinnerList {
  display: grid;
  gap: 16px;
  align-content: start;
  padding: 16px;

  border-top: solid 1px var(--themeColorNeutral100);

  transition: 350ms filter;

  .RunCard--isPast & {
    filter: grayscale(100%);
  }
}

.RunCard__awardWinner {
  display: flex;
  align-items: center;
  color: var(--themeColorNeutral900);
}

.RunCard__awardWinnerAvatar {
  margin-right: 12px;
}

.RunCard__awardWinnerContent {
  display: grid;
  gap: 4px;
  place-items: center start;
  padding: 4px 0;
}

.RunCard__awardWinnerName {
  font: var(--fontWeightMedium) 20px / 1 var(--fontFamilyMono);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
